import { Fragment, useState } from 'react';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import {
  PanelConfig,
  PanelGroup,
  PanelSection,
  PanelTab,
  PointOfInterest,
} from '@/__generated__/graphql';
import { Edit } from '@mui/icons-material';
import { GenericAccordion } from '@/components/shared/GenericAccordion';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { abyssColors } from '@/theme/colors';
import { PanelItemRenderer } from './PanelItemRenderer';
import { panelFeatureRegistry } from './panelFeatureRegistry';
import { FloatingLine, accordionStyles, summaryStyles, tabStyles } from './styles';
import { Pill } from '@/components/shared/Pill';
import { Construction } from '@mui/icons-material';
import { useTriggerPoiEditing } from '../hooks';
import { AllPointOfInterestTemplate } from '@/types';
import { useAccessCheck } from '@/hooks/useAccessCheck';

type Props = {
  config: PanelConfig;
  poi: PointOfInterest;
  template?: AllPointOfInterestTemplate[0];
};

const GroupContent = ({ group, poi }: { group: PanelGroup; poi: PointOfInterest }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const sortedItems = [...group.items].sort((a, b) => a.order - b.order);

  const content = (() => {
    if (group.feature) {
      const FeatureComponent = panelFeatureRegistry[group.feature];
      return FeatureComponent ? (
        <FeatureComponent
          poi={poi}
          sectionKey={group.key}
          isUpdating={isUpdating}
          setIsUpdating={setIsUpdating}
        />
      ) : null;
    }
    return sortedItems.map((item) => <PanelItemRenderer key={item.id} item={item} poi={poi} />);
  })();

  return (
    <Stack gap="1.6rem">
      {group.label && (
        <Typography variant="h5" color={abyssColors.primary[500]}>
          {group.label}
        </Typography>
      )}
      {content}
    </Stack>
  );
};

const SectionContent = ({
  section,
  poi,
  template,
}: {
  section: PanelSection;
  poi: PointOfInterest;
  template?: AllPointOfInterestTemplate[0];
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { triggerEdit } = useTriggerPoiEditing();
  const { canEdit, canView } = useAccessCheck();

  if (!canView(section)) return null;

  const sectionItems = section.groups
    .filter((group) => !group.feature)
    .flatMap((group) => group.items);

  const showEditButton = section.isEditable === true && sectionItems.length > 0 && canEdit(section);

  const editButton = showEditButton ? (
    <IconButton
      disableRipple
      onClick={(event) => {
        event.stopPropagation();
        triggerEdit({
          formState: 'Update-Section',
          pointOfInterest: poi,
          template,
          editingSection: section,
        });
      }}
      aria-label={`Edit ${section.label}`}
      sx={{
        padding: 0,
        '& .MuiSvgIcon-root': { fontSize: '1.6rem', color: abyssColors.primary[300] },
      }}
    >
      <Edit />
    </IconButton>
  ) : undefined;

  const sortedGroups = [...section.groups].sort((a, b) => a.order - b.order);

  const content = (() => {
    if (section.feature) {
      const FeatureComponent = panelFeatureRegistry[section.feature];
      return FeatureComponent ? (
        <FeatureComponent
          poi={poi}
          sectionKey={section.key}
          isUpdating={isUpdating}
          setIsUpdating={setIsUpdating}
        />
      ) : null;
    }
    return (
      <Stack gap="1.6rem">
        {sortedGroups.map((group, index) => (
          <Fragment key={group.id}>
            {index > 0 && (
              <Divider
                sx={{
                  borderColor: abyssColors.primary[100],
                }}
              />
            )}
            <GroupContent group={group} poi={poi} />
          </Fragment>
        ))}
      </Stack>
    );
  })();

  return (
    <GenericAccordion
      summary={section.label}
      openByDefault={!!section?.openByDefault}
      accordionStyles={accordionStyles}
      summaryStyles={summaryStyles}
      endAdornment={editButton}
    >
      {content}
    </GenericAccordion>
  );
};

const TabContent = ({
  tab,
  poi,
  template,
}: {
  tab: PanelTab;
  poi: PointOfInterest;
  template?: AllPointOfInterestTemplate[0];
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const sortedSections = [...tab.sections].sort((a, b) => a.order - b.order);
  const content = (() => {
    if (tab.feature) {
      const FeatureComponent = panelFeatureRegistry[tab.feature];
      return FeatureComponent ? (
        <FeatureComponent
          poi={poi}
          sectionKey={tab.key}
          isUpdating={isUpdating}
          setIsUpdating={setIsUpdating}
        />
      ) : null;
    }
    return sortedSections.map((section) => (
      <SectionContent key={section.id} section={section} poi={poi} template={template} />
    ));
  })();

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: 0,
        overflowY: 'auto',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        paddingTop: '1.2rem',
      }}
    >
      {content}
    </Box>
  );
};

export const DynamicPoiPanel = ({ config, poi, template }: Props) => {
  const sortedTabs = [...config.tabs].sort((a, b) => a.order - b.order);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  if (sortedTabs.length === 0) return null;

  const tabs = sortedTabs.map((tab, index) => ({
    index,
    label: tab.label,
    component: <TabContent tab={tab} poi={poi} template={template} />,
    id: tab.id,
  }));

  return (
    <Stack
      flex={1}
      sx={{
        overflow: 'hidden',
      }}
    >
      <Stack width="100%" alignItems="flex-end" paddingY="0.8rem" paddingRight="2rem">
        <Pill
          label="Work in Progress"
          icon={<Construction />}
          showIcon
          disabled
          state="hover"
          status="failed"
          size="medium"
        />
      </Stack>
      <BasicTabs
        tabs={tabs}
        selectedTab={selectedTabIndex}
        changeTab={setSelectedTabIndex}
        customSeperator={<FloatingLine />}
        tabsStyles={tabStyles}
      />
    </Stack>
  );
};
